import React, { useId, useMemo } from 'react';
import {
  Button,
  Callout,
  Checkbox,
  Combobox,
  type ComboboxOption,
  Dialog,
  Field,
  Input,
} from '../../ui/v2';
import {
  type AgentFormData,
  type PickerOption,
  toComboboxOptions,
  withClearOption,
} from './agentsModel';

type InputChange = (e: React.ChangeEvent<HTMLInputElement>) => void;

const Messages: React.FC<{ error: string; success: string }> = ({
  error,
  success,
}) => (
  <>
    {error && <Callout tone="danger">{error}</Callout>}
    {success && (
      <p role="status" className="text-v2-12 text-v2-success">
        {success}
      </p>
    )}
  </>
);

const RetellLlmModelInput: React.FC<{
  listId: string;
  value: string;
  onChange: InputChange;
  models: readonly string[];
  disabled?: boolean;
}> = ({ listId, value, onChange, models, disabled }) => (
  <Field
    label="Retell LLM Model"
    hint="Optional. Overrides the Retell text LLM on inbound calls. Leave blank to keep the agent's saved model (Retell default is gpt-4.1)."
  >
    {(id, describedBy) => (
      <>
        <Input
          id={id}
          aria-describedby={describedBy}
          type="text"
          name="retell_llm_model"
          list={`${listId}-options`}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder="e.g., gpt-4.1"
        />
        <datalist id={`${listId}-options`}>
          {models.map((model) => (
            <option key={model} value={model} />
          ))}
        </datalist>
      </>
    )}
  </Field>
);

const StorePicker: React.FC<{
  options: PickerOption[];
  value: number;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ options, value, onChange, disabled }) => {
  const comboboxOptions = useMemo(() => toComboboxOptions(options), [options]);
  return (
    <Field label="Store">
      {(id, describedBy) => (
        <Combobox
          id={id}
          aria-describedby={describedBy}
          options={comboboxOptions}
          value={value ? String(value) : ''}
          onChange={onChange}
          placeholder="Select a store..."
          disabled={disabled}
        />
      )}
    </Field>
  );
};

export const CreateAgentDialog: React.FC<{
  open: boolean;
  form: AgentFormData;
  error: string;
  success: string;
  submitting: boolean;
  storeOptions: PickerOption[];
  loadingStores: boolean;
  retellAgentOptions: ComboboxOption[];
  loadingRetellAgents: boolean;
  selectedRetellAgentId: string;
  manualAgentOption: string;
  phoneNumberOptions: ComboboxOption[];
  loadingPhoneNumbers: boolean;
  retellLlmModels: readonly string[];
  onChange: InputChange;
  onStoreChange: (value: string) => void;
  onRetellAgentChange: (value: string) => void;
  onPhoneNumberChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}> = ({
  open,
  form,
  error,
  success,
  submitting,
  storeOptions,
  loadingStores,
  retellAgentOptions,
  loadingRetellAgents,
  selectedRetellAgentId,
  manualAgentOption,
  phoneNumberOptions,
  loadingPhoneNumbers,
  retellLlmModels,
  onChange,
  onStoreChange,
  onRetellAgentChange,
  onPhoneNumberChange,
  onSubmit,
  onClose,
}) => {
  const formId = useId();
  const phonePlaceholder = 'Select an approved unassigned number...';
  const phoneOptions = useMemo(
    () =>
      withClearOption(phoneNumberOptions, form.phone_number, phonePlaceholder),
    [phoneNumberOptions, form.phone_number],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create Agent"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Agent'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <StorePicker
            options={storeOptions}
            value={form.store}
            onChange={onStoreChange}
            disabled={loadingStores}
          />
          <div className="flex flex-col gap-3">
            <Field
              label="Retell Agent"
              hint={
                selectedRetellAgentId === manualAgentOption
                  ? undefined
                  : 'Choose none to enter an ID manually.'
              }
            >
              {(id, describedBy) => (
                <Combobox
                  id={id}
                  aria-describedby={describedBy}
                  options={retellAgentOptions}
                  value={selectedRetellAgentId}
                  onChange={onRetellAgentChange}
                  placeholder="Select a Retell agent..."
                  disabled={loadingRetellAgents}
                />
              )}
            </Field>
            {selectedRetellAgentId === manualAgentOption && (
              <Field
                label="Manual Agent ID"
                hint="Choose none to enter an ID manually."
              >
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    type="text"
                    name="assistant_id"
                    value={form.assistant_id}
                    onChange={onChange}
                    required
                    placeholder="e.g., agent_xxxxxxxxxxxxx"
                  />
                )}
              </Field>
            )}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Agent Provider" hint="Default: retell">
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="assistant_provider"
                value={form.assistant_provider}
                onChange={onChange}
                placeholder="e.g., retell"
              />
            )}
          </Field>
          <Field
            label="Phone Number"
            hint="Optional. Only approved, unassigned Twilio numbers are shown."
          >
            {(id, describedBy) => (
              <Combobox
                id={id}
                aria-describedby={describedBy}
                options={phoneOptions}
                value={form.phone_number}
                onChange={onPhoneNumberChange}
                placeholder={phonePlaceholder}
                disabled={loadingPhoneNumbers}
              />
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Retell Voice ID"
            hint="Optional. Overrides agent voice on inbound calls."
          >
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="retell_voice_id"
                value={form.retell_voice_id}
                onChange={onChange}
                placeholder="e.g., retell-Rita"
              />
            )}
          </Field>
          <RetellLlmModelInput
            listId="create-agent-retell-llm-model"
            value={form.retell_llm_model}
            onChange={onChange}
            models={retellLlmModels}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Language" hint="Default: en">
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="language"
                value={form.language}
                onChange={onChange}
                placeholder="e.g., en"
              />
            )}
          </Field>
        </div>
        <Checkbox
          label="Is Active"
          name="is_active"
          checked={form.is_active}
          onChange={onChange}
        />
        <Messages error={error} success={success} />
      </form>
    </Dialog>
  );
};

export const EditAgentDialog: React.FC<{
  open: boolean;
  form: AgentFormData;
  error: string;
  success: string;
  updating: boolean;
  storeOptions: PickerOption[];
  loadingStores: boolean;
  retellLlmModels: readonly string[];
  onChange: InputChange;
  onStoreChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
}> = ({
  open,
  form,
  error,
  success,
  updating,
  storeOptions,
  loadingStores,
  retellLlmModels,
  onChange,
  onStoreChange,
  onSubmit,
  onClose,
}) => {
  const formId = useId();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Edit Agent"
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={updating}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={formId}
            disabled={updating}
          >
            {updating ? 'Updating...' : 'Update Agent'}
          </Button>
        </>
      }
    >
      <form id={formId} onSubmit={onSubmit} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <StorePicker
            options={storeOptions}
            value={form.store}
            onChange={onStoreChange}
            disabled={loadingStores || updating}
          />
          <Field label="Agent ID">
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="assistant_id"
                value={form.assistant_id}
                onChange={onChange}
                required
                disabled={updating}
                placeholder="e.g., asst_xxxxxxxxxxxxx"
              />
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Agent Provider">
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="assistant_provider"
                value={form.assistant_provider}
                onChange={onChange}
                disabled={updating}
                placeholder="e.g., retell"
              />
            )}
          </Field>
          <Field label="Phone Number">
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="phone_number"
                value={form.phone_number}
                onChange={onChange}
                disabled={updating}
                placeholder="+1234567890"
              />
            )}
          </Field>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field
            label="Retell Voice ID"
            hint="Optional. Overrides agent voice on inbound calls."
          >
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="retell_voice_id"
                value={form.retell_voice_id}
                onChange={onChange}
                disabled={updating}
                placeholder="e.g., retell-Rita"
              />
            )}
          </Field>
          <RetellLlmModelInput
            listId="edit-agent-retell-llm-model"
            value={form.retell_llm_model}
            onChange={onChange}
            models={retellLlmModels}
            disabled={updating}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Field label="Language">
            {(id, describedBy) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                type="text"
                name="language"
                value={form.language}
                onChange={onChange}
                disabled={updating}
                placeholder="en"
              />
            )}
          </Field>
        </div>
        <Checkbox
          label="Is Active"
          name="is_active"
          checked={form.is_active}
          onChange={onChange}
          disabled={updating}
        />
        <Messages error={error} success={success} />
      </form>
    </Dialog>
  );
};
